import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import NoAccess from "./NoAccess";
import { AccessError } from "../services/ApiService";
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { CheckOutlined, CloseOutlined, EditOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import {
  AddSolutionMapping,
  GetSolutionMappings,
  UpdateSolutionMappings,
} from "../services/ApiService";

const { Title } = Typography;

const SOLUTION_TYPES = ["Standalone Solution", "Solution Accelerator"];

function SolutionMapping() {
  const auth = useAuth();
  const token = auth?.user?.id_token;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const [searchText, setSearchText] = useState("");
  const [pageSize, setPageSize] = useState(20);

  // inline edit state
  const [editingKey, setEditingKey] = useState(null);
  const [editingValue, setEditingValue] = useState("");

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchData() {
    setLoading(true);
    setError(null);
    try {
      const result = await GetSolutionMappings(token);
      setData(result.map((row, idx) => ({ ...row, key: idx })));
    } catch (e) {
      if (e instanceof AccessError) {
        setAccessDenied(e.message);
      } else {
        setError(e.message);
      }
    } finally {
      setLoading(false);
    }
  }

  function startEdit(record) {
    setEditingKey(record.key);
    setEditingValue(record.solution_type);
  }

  function cancelEdit() {
    setEditingKey(null);
    setEditingValue("");
  }

  async function saveEdit(record) {
    if (editingValue === record.solution_type) {
      cancelEdit();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = data.map((row) =>
        row.key === record.key ? { ...row, solution_type: editingValue } : row
      );
      const payload = updated.map(({ application_id, solution_type }) => ({
        application_id,
        solution_type,
      }));
      await UpdateSolutionMappings(token, payload);
      setData(updated);
      setSuccess(`Updated ${record.application_id}.`);
      cancelEdit();
      setTimeout(() => setSuccess(null), 5000);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleOnboard(values) {
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      await AddSolutionMapping(token, {
        application_id: values.application_id.trim(),
        solution_type: values.solution_type,
      });
      setSuccess(`Successfully onboarded ${values.application_id.trim()}.`);
      setModalOpen(false);
      form.resetFields();
      await fetchData();
      setTimeout(() => setSuccess(null), 5000);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (accessDenied) {
    return <NoAccess message={accessDenied} />;
  }

  const filteredData = data.filter(
    (row) =>
      row.application_id.toLowerCase().includes(searchText.toLowerCase()) ||
      row.solution_type.toLowerCase().includes(searchText.toLowerCase())
  );

  const solutionTypeFilters = [...new Set(data.map((r) => r.solution_type))].map((t) => ({
    text: t,
    value: t,
  }));

  const columns = [
    {
      title: "Application ID",
      dataIndex: "application_id",
      key: "application_id",
      sorter: (a, b) => a.application_id.localeCompare(b.application_id),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search Application ID"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={confirm}
            style={{ marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button type="primary" onClick={confirm} icon={<SearchOutlined />} size="small">
              Search
            </Button>
            <Button onClick={clearFilters} size="small">
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      ),
      onFilter: (value, record) =>
        record.application_id.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: "Solution Type",
      dataIndex: "solution_type",
      key: "solution_type",
      sorter: (a, b) => a.solution_type.localeCompare(b.solution_type),
      filters: solutionTypeFilters,
      onFilter: (value, record) => record.solution_type === value,
      render: (value, record) => {
        if (editingKey === record.key) {
          return (
            <Select
              value={editingValue}
              onChange={setEditingValue}
              style={{ width: 200 }}
              size="small"
            >
              {SOLUTION_TYPES.map((t) => (
                <Select.Option key={t} value={t}>{t}</Select.Option>
              ))}
            </Select>
          );
        }
        return (
          <Tag color={value === "Solution Accelerator" ? "orange" : "blue"}>{value}</Tag>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      render: (_, record) => {
        if (editingKey === record.key) {
          return (
            <Space>
              <Button
                type="link"
                icon={<CheckOutlined />}
                size="small"
                loading={saving}
                onClick={() => saveEdit(record)}
              />
              <Button
                type="link"
                icon={<CloseOutlined />}
                size="small"
                danger
                onClick={cancelEdit}
              />
            </Space>
          );
        }
        return (
          <Button
            type="link"
            icon={<EditOutlined />}
            size="small"
            disabled={editingKey !== null}
            onClick={() => startEdit(record)}
          >
            Edit
          </Button>
        );
      },
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Title level={3}>Solution Type Mapping</Title>

      {error && (
        <Alert
          message={error}
          type="error"
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}
      {success && (
        <Alert
          message={success}
          type="success"
          closable
          onClose={() => setSuccess(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
        <Input
          placeholder="Search all columns..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setModalOpen(true)}
        >
          Onboard New Solution
        </Button>
      </div>

      <Table
        dataSource={filteredData}
        columns={columns}
        loading={loading}
        pagination={{
          pageSize,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          onShowSizeChange: (_, size) => setPageSize(size),
        }}
        bordered
        size="middle"
      />

      <Modal
        title="Onboard New Solution"
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          form.resetFields();
          setError(null);
        }}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleOnboard}>
          <Form.Item
            label="Application ID"
            name="application_id"
            rules={[{ required: true, message: "Please enter the Application ID." }]}
          >
            <Input placeholder="e.g. SA-12345" />
          </Form.Item>
          <Form.Item
            label="Solution Type"
            name="solution_type"
            rules={[{ required: true, message: "Please select a Solution Type." }]}
          >
            <Select placeholder="Select a solution type">
              {SOLUTION_TYPES.map((t) => (
                <Select.Option key={t} value={t}>
                  {t}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item style={{ textAlign: "right", marginBottom: 0 }}>
            <Space>
              <Button
                onClick={() => {
                  setModalOpen(false);
                  form.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={submitting}>
                Save
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default SolutionMapping;
